import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ROUTES } from "@/app/routes";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { postApiIdentityUsers } from "@/lib/api/generated/identity/identity";
import { useAuth } from "@/features/auth/auth-provider";

export function AdminConversationUserPickerPage() {
  const { isPlatformAdmin } = useAuth();
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  useDocumentTitle("Admin Conversations");

  const usersQuery = useQuery({
    queryKey: ["tenants", tenantId, "admin-conversations", "users", searchText.trim().toLowerCase()],
    queryFn: async () =>
      await postApiIdentityUsers({
        tenantId,
        searchText: searchText.trim() || null,
        page: 1,
        pageSize: 100,
      }),
    enabled: Boolean(isPlatformAdmin && tenantId),
  });

  const users = useMemo(() => usersQuery.data?.items ?? [], [usersQuery.data]);

  if (!isPlatformAdmin) {
    return (
      <EmptyState
        title="Admin access required"
        description="Only platform admins can open filtered conversations for another user."
      />
    );
  }

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="text-base">Admin Conversations</CardTitle>
          <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            Choose a user first, then open their conversations in a dedicated filtered workspace.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 p-4">
          <Input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search by name or email"
          />

          {usersQuery.isLoading ? (
            <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>
              Loading users...
            </div>
          ) : null}

          {usersQuery.error ? (
            <div className="text-sm" style={{ color: "var(--danger)" }}>
              {usersQuery.error instanceof Error ? usersQuery.error.message : "Failed to load users."}
            </div>
          ) : null}

          {!usersQuery.isLoading && users.length === 0 ? (
            <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>
              No users found for this tenant.
            </div>
          ) : null}

          <div className="grid gap-2">
            {users.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => navigate(ROUTES.tenantAdminConversationsUser(tenantId, user.id))}
                className="flex w-full items-center justify-between rounded-md border px-3 py-3 text-left transition hover:bg-accent"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                    {user.displayName || user.id}
                  </div>
                  <div className="truncate text-xs" style={{ color: "var(--muted-foreground)" }}>
                    {user.email}
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm">
                  Open
                </Button>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
